import React, { useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import axios from 'axios';
import BaseLayout from './BaseLayout';
import { buildApiUrl } from '../config/api';
import { tokenFromSearch } from './resetForm';
import { verifyErrorMessage } from './verifyForm';
import '../styles/Login.css';

/**
 * Confirm an email address, using the token from the link sent at sign-up.
 *
 * # WHY A BUTTON AND NOT AUTOMATICALLY ON LOAD
 *
 * Mail scanners open every link in a message before a person does, and some
 * run the page's script. A page that confirmed on load would let the scanner
 * confirm, on the recipient's behalf, an account a stranger registered with
 * their address — and that stranger chose its password. A deliberate click is
 * the recipient saying "this was me".
 *
 * On success it hands off to /login with a notice, the same handover
 * ResetPassword uses.
 */
const VerifyEmail = () => {
  const navigate = useNavigate();
  const token = tokenFromSearch(useLocation().search);
  const [error, setError] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleConfirm = async () => {
    if (isSubmitting) return;
    setIsSubmitting(true);
    setError('');
    try {
      await axios.post(buildApiUrl('/auth/verify-email'), { token });
      navigate('/login', {
        replace: true,
        state: { notice: 'Your email is confirmed. Sign in to get started.' },
      });
    } catch (err) {
      setError(verifyErrorMessage(err));
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <BaseLayout>
      <div className="login">
        <div className="card login-card">
          <header className="login-header">
            <span className="pill">Confirm email</span>
            <h1 className="page-title login-title">
              {token ? 'Confirm your email' : 'This link is incomplete'}
            </h1>
            {token && (
              <p className="meta-copy login-subtitle">
                Only confirm if you signed up. If you did not, close this page and
                nothing will happen.
              </p>
            )}
          </header>

          {!token && (
            <div className="status-error" role="alert">
              That confirmation link is missing its token. Open the link from the
              email again, or reset your password — that confirms your email too.
            </div>
          )}

          {error && (
            <div className="status-error" role="alert">
              {error}
            </div>
          )}

          <div className="login-actions">
            {token && (
              <button
                type="button"
                className="btn-primary"
                onClick={handleConfirm}
                disabled={isSubmitting}
              >
                {isSubmitting ? 'Confirming…' : 'Confirm my email'}
              </button>
            )}
            <Link to="/forgot-password" className="btn-ghost">
              Reset my password
            </Link>
          </div>
        </div>
      </div>
    </BaseLayout>
  );
};

export default VerifyEmail;
